/**
 * Keep the Google click identifiers in the app URL for the whole funnel.
 *
 * Why: the Google Ads conversions `newLead` / `newQuote` / `newPolicy` are fired
 * server-side (GTM web -> sGTM `analytics.life5.xx` -> Google Ads). Since the app
 * runs Consent Mode v2 (2026-09-10) a visitor who declines advertising storage
 * has no `_gcl_aw` cookie, and the only remaining way for the server-side tag
 * to attribute the conversion is the click id in the page URL (`page_location`)
 * — exactly what happens on the ES landing page, where the lead is created on
 * the marketing site and the URL still carries `?gclid=`. Inside the SPA the
 * router drops the query string on the first navigation (`/calculator/3`), so
 * the conversion reached Google without click id and was discarded (IT lost
 * ~45 % of `newLead` from 2026-09-10; ES leads created in the app were affected
 * in the same way, just fewer of them).
 *
 * The click id travels to the app in the handoff URL built by the marketing
 * site (`appHandoff.ts`), is sealed here in sessionStorage (like the web does
 * with the landing context, because the CMP may delete first-party cookies
 * while consent is pending) and is re-appended to the URL on every route
 * change. No cookie is written by this module.
 */

export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];
export type ClickIds = Partial<Record<ClickIdKey, string | null | undefined>>;

const STORAGE_KEY = "life5:click-ids:v1";

const isUsable = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.length > 0 && value !== "null" && value !== "undefined";

const storage = (): Storage | null => {
  try {
    return typeof window !== "undefined" && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

/** Click ids present in a query string. */
export const clickIdsFromQuery = (query: URLSearchParams): ClickIds => {
  const out: ClickIds = {};
  CLICK_ID_KEYS.forEach((key) => {
    const value = query.get(key);
    if (isUsable(value)) out[key] = value;
  });
  return out;
};

/** Persist for the tab session the click ids found on arrival (idempotent, never removes). */
export const sealClickIds = (ids: ClickIds): void => {
  const s = storage();
  if (!s) return;
  const merged = { ...readSealedClickIds(), ...pickUsable(ids) };
  if (!Object.keys(merged).length) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* quota / privacy mode: the in-memory context still has the ids */
  }
};

export const readSealedClickIds = (): ClickIds => {
  const s = storage();
  if (!s) return {};
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? pickUsable(parsed) : {};
  } catch {
    return {};
  }
};

const pickUsable = (ids: ClickIds): ClickIds => {
  const out: ClickIds = {};
  CLICK_ID_KEYS.forEach((key) => {
    const value = ids[key];
    if (isUsable(value)) out[key] = value;
  });
  return out;
};

/** First usable value wins: URL, then in-memory context, then the sealed copy. */
export const resolveClickIds = (...sources: ClickIds[]): ClickIds => {
  const out: ClickIds = {};
  CLICK_ID_KEYS.forEach((key) => {
    for (const src of sources) {
      const value = src[key];
      if (isUsable(value)) {
        out[key] = value;
        break;
      }
    }
  });
  return out;
};

/**
 * Query string with the missing click ids appended. Returns `null` when the
 * URL already carries every known id (nothing to do), so callers can avoid a
 * redundant navigation. Existing params are never modified or reordered.
 */
export const withClickIds = (search: string, ids: ClickIds): string | null => {
  const params = new URLSearchParams(search);
  let changed = false;
  CLICK_ID_KEYS.forEach((key) => {
    const value = ids[key];
    if (isUsable(value) && !isUsable(params.get(key))) {
      params.append(key, value);
      changed = true;
    }
  });
  if (!changed) return null;
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};
